import React, { useId, useRef, useState } from "react"
import { Upload } from "lucide-react"
import Button from "../common/Button.jsx"
import OrderFileDeleteModal from "./OrderFileDeleteModal.jsx"
import OrderFileList from "./OrderFileList.jsx"
import {
    formatFileSize as defaultFormatFileSize,
    getFilename,
    uniqueFilename,
} from "./orderFileManagerUtils.js"
import "./orders.css"

/**
 * Order attachment manager: immediate upload/delete via api.uploadFile / api.deleteFile.
 * @param {{
 *   orderId: number | string,
 *   files?: any[],
 *   api: { uploadFile: Function, deleteFile: Function },
 *   apiOptions?: Record<string, unknown>,
 *   formatFileSize?: (bytes: number) => string,
 *   onChange?: () => Promise<void> | void,
 *   onError?: (error: unknown) => void,
 *   onSuccess?: (message: string) => void,
 * }} props
 */
export default function OrderFileManager({
    orderId,
    files = [],
    api,
    apiOptions,
    formatFileSize = defaultFormatFileSize,
    onChange = async () => {},
    onError = (_error) => {},
    onSuccess = (_message) => {},
}) {
    const inputId = useId()
    const inputRef = useRef(/** @type {HTMLInputElement | null} */ (null))
    const [dragActive, setDragActive] = useState(false)
    const [uploading, setUploading] = useState(/** @type {{ key: string, filename: string, sizeLabel: string }[]} */ ([]))
    const [pendingDelete, setPendingDelete] = useState(/** @type {string | null} */ (null))
    const [isDeleting, setIsDeleting] = useState(false)

    const savedFiles = Array.isArray(files) ? files : []
    const existingNames = [...savedFiles.map(getFilename), ...uploading.map((row) => row.filename)]
    const isBusy = uploading.length > 0 || isDeleting
    const hasFiles = savedFiles.length > 0 || uploading.length > 0

    function openFileDialog() {
        inputRef.current?.click()
    }

    async function uploadFiles(fileList) {
        if (!orderId || !api?.uploadFile || !fileList?.length) {
            return
        }
        const namesInBatch = [...existingNames]
        for (const file of [...fileList]) {
            const filename = uniqueFilename(file.name, namesInBatch)
            if (!filename) {
                onError(new Error(`Could not create a unique name for ${file.name}`))
                continue
            }
            namesInBatch.push(filename)
            const key = `${filename}-${Date.now()}-${Math.random().toString(36).slice(2)}`
            const renamed = new File([file], filename, { type: file.type })
            setUploading((prev) => [...prev, { key, filename, sizeLabel: formatFileSize(file.size) }])
            try {
                if (apiOptions) {
                    await api.uploadFile(orderId, renamed, filename, apiOptions)
                } else {
                    await api.uploadFile(orderId, renamed, filename)
                }
                onSuccess(`Uploaded ${filename}`)
                await onChange()
            } catch (error) {
                onError(error)
            } finally {
                setUploading((prev) => prev.filter((row) => row.key !== key))
            }
        }
    }

    function onFileSelected(event) {
        void uploadFiles(event.currentTarget.files)
        event.currentTarget.value = ""
    }

    function onDrop(event) {
        event.preventDefault()
        setDragActive(false)
        void uploadFiles(event.dataTransfer?.files)
    }

    async function confirmDelete() {
        const filename = pendingDelete
        if (!orderId || !filename || !api?.deleteFile || isDeleting) {
            return
        }
        setIsDeleting(true)
        try {
            if (apiOptions) {
                await api.deleteFile(orderId, filename, apiOptions)
            } else {
                await api.deleteFile(orderId, filename)
            }
            setPendingDelete(null)
            onSuccess(`Deleted ${filename}`)
            await onChange()
        } catch (error) {
            onError(error)
        } finally {
            setIsDeleting(false)
        }
    }

    return (
        <div className="order-file-manager w-100 h-100 d-flex flex-column">
            <div
                className={`order-file-manager__dropzone flex-grow-1 d-flex flex-column${dragActive ? " order-file-manager__dropzone--active" : ""}`}
                onDrop={onDrop}
                onDragOver={(event) => { event.preventDefault(); setDragActive(true) }}
                onDragLeave={(event) => {
                    event.preventDefault()
                    if (!event.currentTarget.contains(/** @type {Node} */ (event.relatedTarget))) {
                        setDragActive(false)
                    }
                }}
            >
                {!hasFiles ? (
                    <button
                        type="button"
                        className="order-file-manager__empty btn btn-link text-decoration-none text-secondary w-100 flex-grow-1 py-4 py-md-5"
                        onClick={openFileDialog}
                        disabled={!orderId || isBusy}
                    >
                        <Upload className="mb-2" size={36} aria-hidden />
                        <div className="fs-5 fw-semibold text-dark">Browse Files</div>
                        <div className="small mt-1">Tap to choose, or drag and drop</div>
                    </button>
                ) : (
                    <>
                        <div className="order-file-manager__toolbar">
                            <h3 className="order-file-manager__title fs-5 fw-semibold text-dark mb-0">Files</h3>
                            <Button variant="secondary" size="sm" onClick={openFileDialog} disabled={!orderId || isBusy}>
                                Browse...
                            </Button>
                            <span className="text-secondary small">Or drop files here</span>
                        </div>
                        <OrderFileList
                            uploading={uploading}
                            savedFiles={savedFiles}
                            isBusy={isBusy}
                            onDelete={setPendingDelete}
                        />
                    </>
                )}
                <input id={inputId} ref={inputRef} type="file" multiple className="d-none" onChange={onFileSelected} />
            </div>

            <OrderFileDeleteModal
                filename={pendingDelete}
                isDeleting={isDeleting}
                onCancel={() => setPendingDelete(null)}
                onConfirm={() => { void confirmDelete() }}
            />
        </div>
    )
}
